import { useEffect, useRef } from "react";
import { useRecoilState } from "recoil";
import { trainingStateAtom, validatingStateAtom } from "../recoil/upgrade";
import { swBridge } from "../utils/sw-register";
import { BASE_URL } from "../services/client";
import { toast } from "react-toastify";
import { resultItemType } from "../components/new/TrainingProgressDropdown";
import userEvent from "@testing-library/user-event";
import { stopEpochTrain } from "../services/trainService";

export interface EpochResult {
  model_uuid: string;
  performance: Record<string, number>;
  epoch: number;
  num_train: number;
  sec_elapsed: number;
  state: "train" | "valid" | "done";
}

interface EpochStreamProps {
  enabled: boolean;
  trainUuid: string | null;
  projectData: any;
  myAIModule: any;
  id: string | undefined;
  hasValidModelRef: React.MutableRefObject<Record<number, boolean>>;
  setMyAIModule: (module: any) => void;
  onProgress: (data: { count: number; totalCount: number; epochResults: resultItemType[] }) => void;
  onValidProgress: (data: { extracted: any[]; count: number; totalCount: number }) => void;
  onComplete: () => void;
  onError: (err: any) => void;
}

const SW_AVAILABLE = "serviceWorker" in navigator;
const WATCHDOG_INTERVAL = 10000;

export function useEpochStream({
  enabled,
  trainUuid,
  projectData,
  myAIModule,
  id,
  hasValidModelRef,
  setMyAIModule,
  onProgress,
  onValidProgress,
  onComplete,
  onError,
}: EpochStreamProps) {
  const [, setTrainingState] = useRecoilState(trainingStateAtom);
  const [, setValidatingState] = useRecoilState(validatingStateAtom);
  const isFinishedRef = useRef(false);
  const pollingStoppedRef = useRef(false); // 의도적으로 멈춘 경우 watchdog이 되살리지 않도록
  const lastMsgAtRef = useRef(0);
  // SW 리스너는 effect 재실행 전까지 같은 함수라 콜백을 직접 쓰면 등록 시점의 state(validList 등)를 본다.
  // 항상 최신 콜백을 ref로 읽는다 (useTrainingStream과 같은 방식)
  const latestRef = useRef({ myAIModule, onProgress, onValidProgress, onComplete, onError });
  latestRef.current = { myAIModule, onProgress, onValidProgress, onComplete, onError };

  const handleDone = (uuid: string) => {
    if (isFinishedRef.current) return;
    isFinishedRef.current = true;

    if (SW_AVAILABLE) {
      pollingStoppedRef.current = true;
      swBridge.stopPolling(uuid);
      swBridge.clearCache(uuid);
    }

    const { myAIModule } = latestRef.current;
    setMyAIModule({
      ...myAIModule,
      desc: { ...myAIModule.desc, train: false, preview: true },
    });

    setTrainingState((prev: any) => ({ ...prev, [Number(id)]: false }));
    setValidatingState((prev: any) => ({ ...prev, [Number(id)]: false }));
    latestRef.current.onComplete();
  };

  const handleError = (uuid: string, error: any) => {
    if (isFinishedRef.current) return;
    isFinishedRef.current = true;

    if (SW_AVAILABLE) {
      pollingStoppedRef.current = true;
      swBridge.stopPolling(uuid);
      swBridge.clearCache(uuid);
    }

    toast.error("학습 중 오류가 발생했습니다");
    setTrainingState((prev: any) => ({ ...prev, [Number(id)]: false }));
    const { myAIModule } = latestRef.current;
    setMyAIModule({
      ...myAIModule,
      desc: { ...myAIModule.desc, train: false, preview: true },
    });
    latestRef.current.onError(error);
  };

  useEffect(() => {
    if (!enabled || !trainUuid || !myAIModule || !projectData) return;
    if (!SW_AVAILABLE) {
      console.warn("[useEpochTrainStream] SW 미지원 환경.");
      return;
    }

    isFinishedRef.current = false;
    pollingStoppedRef.current = false;
    lastMsgAtRef.current = Date.now();
    // ip: 백엔드 재시작 후 polling 재개용
    const ip = encodeURIComponent(myAIModule?.serverAddress ?? "");
    const pollUrl = `${BASE_URL}/ai/training/epoch/${trainUuid}?ip=${ip}`;
    swBridge.startPolling(trainUuid, pollUrl);

    // 브라우저는 유휴 SW를 수십 초 만에 종료하고, 그러면 폴링 루프가 조용히 사라진다
    // (요청만 끊기고 에러는 안 난다 = 진행도가 멈춘 채로 보임). 주기적으로 kick해서 되살린다.
    // 폴링이 살아있으면 SW쪽 중복 가드에 걸려 no-op이라 평소엔 아무 일도 안 한다.
    const kick = () => {
      if (pollingStoppedRef.current) return;
      const idle = Date.now() - lastMsgAtRef.current;
      if (idle > WATCHDOG_INTERVAL) console.warn(`[SW] ${idle}ms 동안 진행도 수신 없음: ${trainUuid}`);
      swBridge.startPolling(trainUuid, pollUrl, true);
    };
    const watchdog = setInterval(kick, WATCHDOG_INTERVAL);
    // 백그라운드 탭은 setInterval이 1분까지 throttle되므로 돌아오는 즉시 한 번 더
    const onVisible = () => document.visibilityState === "visible" && kick();
    document.addEventListener("visibilitychange", onVisible);

    fetch(`/sw-cache/progress/${trainUuid}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.state === "running" && data.type === "epoch_train") {
          latestRef.current.onProgress({
            count: data.count,
            totalCount: data.totalCount,
            epochResults: data.epochResults ?? [],
          });
        }
      })
      .catch(() => {});

    const handleSWMessage = (event: MessageEvent) => {
      const { type, uuid, data } = event.data ?? {};
      if (type !== "PROGRESS_UPDATE" || uuid !== trainUuid) return;
      lastMsgAtRef.current = Date.now();
      if (!data) return;

      if (data.state === "not_found_limit") {
        handleError(uuid, new Error("epoch progress not found"));
        return;
      }

      if (isFinishedRef.current) return;

      if (data.state === "running") {
        if (data.type === "epoch_train") {
          latestRef.current.onProgress({
            count: data.count,
            totalCount: data.totalCount,
            epochResults: data.epochResults ?? [],
          });
        }
        if (data.type === "valid") {
          const extracted = (data.items ?? []).map((item: any) => ({
            id: item.path,
            AILabel: item.label,
            score: item.score,
            path: item.path,
          }));
          console.log("extracted", extracted);
          latestRef.current.onValidProgress?.({ extracted, count: data.count, totalCount: data.totalCount });
        }
        return;
      }

      if (data.state === "stalled") {
        stopEpochTrain({
          uuid: id!!,
          ip: myAIModule?.serverAddress,
        });

        setTrainingState((prev) => ({ ...prev, [Number(id)]: false }));
        pollingStoppedRef.current = true;
        swBridge.stopPolling(uuid);
        swBridge.clearCache(uuid);
        toast.success("AI 오류로 인해 학습/검사가 중단되었습니다.");
      }

      if (data.state === "done") {
        if (data.type === "epoch_train" && data.epochState === "stop") {
          if (isFinishedRef.current) return;
          const epochResults = data.epochResults ?? [];

          const hasValidModel = data.epochState === "stop" ? epochResults.some((r: any) => r.model_uuid !== "") : true;

          hasValidModelRef.current[Number(id)] = hasValidModel;

          latestRef.current.onProgress({
            count: data.totalCount,
            totalCount: data.totalCount,
            epochResults,
          });

          handleDone(uuid);
          return;
        }
      }

      if (data.state === "error") {
        handleError(uuid, data.error);
        return;
      }
    };

    navigator.serviceWorker.addEventListener("message", handleSWMessage);

    return () => {
      clearInterval(watchdog);
      document.removeEventListener("visibilitychange", onVisible);
      navigator.serviceWorker.removeEventListener("message", handleSWMessage);
    };
  }, [enabled, trainUuid, projectData, myAIModule]);
}
